/**
 * Shared Who-column initials + chip colours (Tx Review, VAT, etc.).
 *
 * Palette (fixed):
 * - Om → blue
 * - Dw → yellow
 * - Le → orange
 * - He → green
 * - EFT → grey (no cardholder / bank transfer)
 * - ? → muted grey (unrecognized cardholder name)
 */

export type WhoInitials = 'Om' | 'Dw' | 'Le' | 'He' | 'EFT' | '?';

export type WhoChipSx = {
  backgroundColor: string;
  color: string;
  borderColor: string;
};

/** Om=Omar, Dw=Dwayne, Le=Lena, He=Heidi; empty → EFT; else ?. */
export function whoInitialsFromCardholder(raw: string | null | undefined): WhoInitials {
  const s = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  if (!s) return 'EFT';
  const first = (s.split(' ')[0] || '').replace(/[^a-z]/g, '');
  if (first.startsWith('omar')) return 'Om';
  if (first.startsWith('dwayne')) return 'Dw';
  if (first.startsWith('lena')) return 'Le';
  if (first.startsWith('heidi')) return 'He';
  if (/\bomar\b/.test(s)) return 'Om';
  if (/\bdwayne\b/.test(s)) return 'Dw';
  if (/\blena\b/.test(s)) return 'Le';
  if (/\bheidi\b/.test(s)) return 'He';
  return '?';
}

/** Stable filled-chip colours for the Who column. */
export function whoChipSx(who: WhoInitials): WhoChipSx {
  switch (who) {
    case 'Om': // blue
      return { backgroundColor: '#DBEAFE', color: '#1E3A8A', borderColor: '#93C5FD' };
    case 'Dw': // yellow
      return { backgroundColor: '#FEF08A', color: '#713F12', borderColor: '#FACC15' };
    case 'Le': // orange
      return { backgroundColor: '#FFEDD5', color: '#9A3412', borderColor: '#FB923C' };
    case 'He': // green
      return { backgroundColor: '#DCFCE7', color: '#14532D', borderColor: '#86EFAC' };
    case 'EFT': // grey
      return { backgroundColor: '#E5E7EB', color: '#374151', borderColor: '#9CA3AF' };
    default: // unrecognized
      return { backgroundColor: '#F3F4F6', color: '#6B7280', borderColor: '#D1D5DB' };
  }
}

export function whoTooltipLabel(fullName: string | null | undefined, who: WhoInitials): string {
  const name = String(fullName || '').trim();
  if (name) return name;
  if (who === 'EFT') return 'Electronic Funds Transfer';
  return 'Unknown cardholder';
}
